import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { Badge } from '../../ui/badge';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../../ui/alert-dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../ui/dropdown-menu';
import {
  Users,
  Plus,
  Edit,
  Trash2,
  MoreVertical,
  Mail,
  Phone,
  Briefcase,
  User,
  Star,
  Smartphone
} from 'lucide-react';
import { procurementService, VendorContact, VendorContactCreate, VendorContactUpdate } from '../../../src/services/procurement.service';
import { showSuccess, showError } from '../../../src/utils/notification.utils';
import { authService } from '../../../src/services/auth.service';

interface VendorContactsProps {
  vendorId: string;
  vendorName: string;
}

interface ContactModalProps {
  mode: 'add' | 'edit';
  vendorId: string;
  contact?: VendorContact;
  trigger: React.ReactNode;
  onSuccess: () => void;
}

function ContactModal({ mode, vendorId, contact, trigger, onSuccess }: ContactModalProps) {
  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    contact_name: contact?.contact_name || '',
    title: contact?.title || '',
    department: contact?.department || '',
    email: contact?.email || '',
    phone: contact?.phone || '',
    mobile: contact?.mobile || '',
    is_primary: contact?.is_primary || false,
    notes: contact?.notes || ''
  });

  const [errors, setErrors] = useState<{[key: string]: string}>({});

  const validateForm = () => {
    const newErrors: {[key: string]: string} = {};

    if (!formData.contact_name.trim()) {
      newErrors.contact_name = 'Contact name is required';
    }
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      showError({
        title: 'Validation Error',
        message: 'Please fill in all required fields correctly'
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const userProfile = await authService.getUserProfile();

      if (mode === 'add') {
        await procurementService.createVendorContact(vendorId, formData as VendorContactCreate, userProfile.organizationId);
        showSuccess({
          title: 'Contact Added',
          message: `${formData.contact_name} has been added successfully`
        });
      } else if (mode === 'edit' && contact) {
        await procurementService.updateVendorContact(contact.contact_id, formData as VendorContactUpdate);
        showSuccess({
          title: 'Contact Updated',
          message: `${formData.contact_name} has been updated successfully`
        });
      }

      setOpen(false);
      onSuccess();
    } catch (error: any) {
      console.error('Failed to save contact:', error);
      showError({
        title: mode === 'add' ? 'Failed to Add Contact' : 'Failed to Update Contact',
        message: error?.message || 'An unexpected error occurred'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            {mode === 'add' ? 'Add New Contact' : 'Edit Contact'}
          </DialogTitle>
          <DialogDescription>
            {mode === 'add' ? 'Add a new contact person for this vendor' : 'Update contact information'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="contact-name">Contact Name *</Label>
              <Input
                id="contact-name"
                value={formData.contact_name}
                onChange={(e) => setFormData({...formData, contact_name: e.target.value})}
                placeholder="John Doe"
              />
              {errors.contact_name && <p className="text-sm text-destructive">{errors.contact_name}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                placeholder="Sales Manager"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="department">Department</Label>
            <Input
              id="department"
              value={formData.department}
              onChange={(e) => setFormData({...formData, department: e.target.value})}
              placeholder="Sales"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                placeholder="john.doe@company.com"
              />
              {errors.email && <p className="text-sm text-destructive">{errors.email}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                placeholder="+1-555-0123"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="mobile">Mobile</Label>
            <Input
              id="mobile"
              value={formData.mobile}
              onChange={(e) => setFormData({...formData, mobile: e.target.value})}
              placeholder="+1-555-0124"
            />
          </div>

          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="is-primary"
              checked={formData.is_primary}
              onChange={(e) => setFormData({...formData, is_primary: e.target.checked})}
              className="rounded border-border"
            />
            <Label htmlFor="is-primary" className="cursor-pointer">
              Set as primary contact
            </Label>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              placeholder="Additional notes about this contact..."
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" disabled={isSubmitting} onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : mode === 'add' ? 'Add Contact' : 'Update Contact'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function VendorContacts({ vendorId, vendorName }: VendorContactsProps) {
  const [contacts, setContacts] = useState<VendorContact[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchContacts = async () => {
    try {
      setLoading(true);
      console.log('[VendorContacts] Fetching contacts for vendorId:', vendorId);
      const response = await procurementService.getVendorContacts(vendorId);
      console.log('[VendorContacts] API Response:', response);
      console.log('[VendorContacts] Response.data:', response.data);
      const contactsData = Array.isArray(response.data) ? response.data : (response.data?.data || []);
      console.log('[VendorContacts] Contacts loaded:', contactsData.length, contactsData);
      setContacts(contactsData);
    } catch (error) {
      console.error('[VendorContacts] Failed to fetch contacts:', error);
      showError({
        title: 'Failed to Load Contacts',
        message: 'Unable to load vendor contacts'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContacts();
  }, [vendorId]);

  const handleDelete = async (contact: VendorContact) => {
    try {
      await procurementService.deleteVendorContact(contact.contact_id);
      showSuccess({
        title: 'Contact Deleted',
        message: `${contact.contact_name} has been removed`
      });
      fetchContacts();
    } catch (error: any) {
      showError({
        title: 'Failed to Delete Contact',
        message: error?.message || 'An unexpected error occurred'
      });
    }
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="text-muted-foreground">Loading contacts...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-medium">Contacts</h3>
          <p className="text-sm text-muted-foreground">Manage contact persons for {vendorName}</p>
        </div>
        <ContactModal
          mode="add"
          vendorId={vendorId}
          onSuccess={fetchContacts}
          trigger={
            <Button size="sm">
              <Plus className="h-4 w-4 mr-2" />
              Add Contact
            </Button>
          }
        />
      </div>

      {contacts.length === 0 ? (
        <div className="text-center py-12 border rounded-lg">
          <Users className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium mb-2">No contacts yet</h3>
          <p className="text-muted-foreground mb-4">Add contact persons to manage communication with this vendor</p>
          <ContactModal
            mode="add"
            vendorId={vendorId}
            onSuccess={fetchContacts}
            trigger={
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Add First Contact
              </Button>
            }
          />
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Title / Department</TableHead>
              <TableHead>Contact Information</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {contacts.map((contact) => (
              <TableRow key={contact.contact_id}>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="font-medium">{contact.contact_name}</p>
                      {contact.is_primary && (
                        <Badge variant="outline" className="mt-1">
                          <Star className="h-3 w-3 mr-1 fill-yellow-500 text-yellow-500" />
                          Primary
                        </Badge>
                      )}
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="space-y-1">
                    {contact.title && (
                      <div className="flex items-center gap-2 text-sm">
                        <Briefcase className="h-3 w-3 text-muted-foreground" />
                        {contact.title}
                      </div>
                    )}
                    {contact.department && (
                      <p className="text-sm text-muted-foreground">{contact.department}</p>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="space-y-1">
                    {contact.email && (
                      <div className="flex items-center gap-2 text-sm">
                        <Mail className="h-3 w-3 text-muted-foreground" />
                        <a href={`mailto:${contact.email}`} className="text-primary hover:underline">
                          {contact.email}
                        </a>
                      </div>
                    )}
                    {contact.phone && (
                      <div className="flex items-center gap-2 text-sm">
                        <Phone className="h-3 w-3 text-muted-foreground" />
                        <a href={`tel:${contact.phone}`} className="hover:underline">
                          {contact.phone}
                        </a>
                      </div>
                    )}
                    {contact.mobile && (
                      <div className="flex items-center gap-2 text-sm">
                        <Smartphone className="h-3 w-3 text-muted-foreground" />
                        <a href={`tel:${contact.mobile}`} className="hover:underline">
                          {contact.mobile}
                        </a>
                      </div>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  {contact.is_primary ? (
                    <Badge className="bg-green-100 text-green-800 border-green-200">Primary</Badge>
                  ) : (
                    <Badge variant="outline">Secondary</Badge>
                  )}
                </TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <ContactModal
                        mode="edit"
                        vendorId={vendorId}
                        contact={contact}
                        onSuccess={fetchContacts}
                        trigger={
                          <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                            <Edit className="h-4 w-4 mr-2" />
                            Edit Contact
                          </DropdownMenuItem>
                        }
                      />
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <DropdownMenuItem className="text-destructive" onSelect={(e) => e.preventDefault()}>
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete Contact
                          </DropdownMenuItem>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Contact</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to delete {contact.contact_name}? This action cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => handleDelete(contact)} className="bg-destructive hover:bg-destructive/90">
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
